import { httpclient } from "@kce2/common/core/request";
import type { KscResponseModel, Request } from "@kce2/common/core/request";

export interface AZInfo {
  azCode: string;
  azName: string;
  enName?: string;
}

/**
 * @example
 * {
 *   InnerCode: "SHPBSVpctestRegionOne"
 *   RegionCode: "cn-shanghai-3"
 *   RegionName: "华东2（上海）"
 *   active: true
 *   azList: [{azCode: "cn-shanghai-3a", azName: "可用区A", enName: "cn-shanghai-3a"}]
 *   regionId: 2
 * }
 */
export interface Region {
  InnerCode: string;
  RegionCode: string;
  RegionName: string;
  RegionEnName: string;
  active: boolean;
  azList: AZInfo[];
  areaCode?: string;
  areaName?: string;
  areaEnName?: string;
  regionId: number;
}

export const NamespaceType = {
  Persistent: 1,
  Dynamic: 0,
};

export type Namespace = {
  Name: string;
  Description: string;
  CreateTime: string;
  UpdateTime: string;
  /// please use NamespaceType
  Type: number;
};

type ZF<R = any> = () => R;
type AF<R = any, A = any> = (arg: A) => R;

function isZeroArg<R>(fn: AF<R> | ZF<R>): fn is ZF<R> {
  return fn.length === 0;
}

function injectRegionHeader<K>(fn: () => Request<K>): (arg: { Region: string }) => Request<K>;
function injectRegionHeader<K, A>(
  fn: (a: A) => Request<K>,
): (arg: A & { Region: string }) => Request<K>;
function injectRegionHeader<R extends Request<any>, A>(fn: AF<R, A> | ZF<R>) {
  if (isZeroArg(fn)) {
    return function ({ Region }: { Region: string }) {
      const req = fn();
      req.headers = {
        ...req.headers,
        "X-KSC-REGION": Region,
      };
      return req;
    };
  }

  return function (arg: A & { Region: string }) {
    const req = fn(arg);
    req.headers = {
      ...req.headers,
      "X-KSC-REGION": arg.Region,
    };
    return req;
  };
}

export type GetResponseResponse = KscResponseModel<{ Regions: null | Region[] } | Region[]>;

export function getRegions() {
  return httpclient.get<GetResponseResponse>({
    url: `/api/cluster?Action=DescribeRegion`,
  });
}

export type CheckProductResponse = KscResponseModel<boolean>;

export const checkProductPermission = injectRegionHeader(function () {
  return httpclient.get<CheckProductResponse>({
    url: "/commons/kcf/checkApply",
  });
});

export type CheckApplyResponse = never;

export function checkApply() {
  return httpclient.get<CheckApplyResponse>({
    url: "/commons/kcf/checkProbationApply",
  });
}

export type ApplyProductPermissionResponse = never;

export const applyProductPermission = injectRegionHeader(function () {
  return httpclient.post<ApplyProductPermissionResponse>({
    url: "/commons/kcf/apply",
  });
});

export type CheckEventBusPermissionResponse = KscResponseModel<boolean>;

export const checkEventBusPermission = injectRegionHeader(function () {
  return httpclient.get<CheckEventBusPermissionResponse>({
    url: "/commons/eventbus/checkApply",
  });
});

export type OpenEventBusPermissionResponse = KscResponseModel<boolean>;

export const openEventBusPermission = injectRegionHeader(function () {
  return httpclient.post<OpenEventBusPermissionResponse>({
    url: "/commons/eventbus/apply",
  });
});

export type CheckRolePermissionResponse = KscResponseModel<boolean>;

export const checkRolePermission = function () {
  return httpclient.get<CheckRolePermissionResponse>({
    url: "/api/cluster?Action=VerifyKceRole",
  });
};
